import type { Session } from '@supabase/supabase-js';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@nuberu/ui';
import { useState, type FormEvent, type ReactElement, type ReactNode } from 'react';

import { ApiError } from '@/lib/api';

import { fetchLookup, resendConfirmation, type LookupResult } from './fetch-lookup';

type ActionState = 'idle' | 'busy' | 'done' | 'error';

// Resend the booking confirmation email. Local state so the button reflects its
// own progress independently of the search.
const ResendButton = ({
  session,
  bookingId,
}: {
  session: Session;
  bookingId: string;
}): ReactElement => {
  const [state, setState] = useState<ActionState>('idle');

  const click = async (): Promise<void> => {
    setState('busy');
    try {
      await resendConfirmation(session, bookingId);
      setState('done');
    } catch {
      setState('error');
    }
  };

  if (state === 'done') {
    return <span className="text-xs text-status-success">confirmación reenviada ✓</span>;
  }
  return (
    <Button variant="neutral" size="sm" disabled={state === 'busy'} onClick={() => void click()}>
      {state === 'busy'
        ? 'Enviando…'
        : state === 'error'
          ? 'Reenviar confirmación ⚠'
          : 'Reenviar confirmación'}
    </Button>
  );
};

type State =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'done'; result: LookupResult };

const Field = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <div className="flex items-start justify-between gap-4 border-b border-(color:--nuberu-line-soft) py-2 last:border-b-0">
    <span className="text-sm text-text-secondary">{label}</span>
    <span className="text-right text-sm font-medium text-text-primary">{children}</span>
  </div>
);

const mono = (value: string | null): ReactElement =>
  value === null ? (
    <span className="text-text-secondary">—</span>
  ) : (
    <code className="font-mono text-xs">{value}</code>
  );

const ResultView = ({
  result,
  session,
}: {
  result: LookupResult;
  session: Session;
}): ReactElement => {
  if (result.kind === 'none') {
    return <p className="py-2 text-sm text-text-secondary">Sin resultados.</p>;
  }
  if (result.kind === 'operator') {
    const o = result.operator;
    return (
      <div className="flex flex-col">
        <Field label="Tipo">Operador</Field>
        <Field label="Nombre">{o.name}</Field>
        <Field label="Slug">{mono(o.slug)}</Field>
        <Field label="ID">{mono(o.id)}</Field>
        <Field label="Página de reservas">{o.hostedPageEnabled ? 'activa' : 'inactiva'}</Field>
        <Field label="Creado">{o.createdAt.slice(0, 10)}</Field>
      </div>
    );
  }
  if (result.kind === 'booking') {
    const b = result.booking;
    return (
      <div className="flex flex-col">
        <Field label="Tipo">Reserva</Field>
        <Field label="ID">{mono(b.id)}</Field>
        <Field label="Operador">{mono(b.operatorSlug)}</Field>
        <Field label="Estado">{b.state}</Field>
        <Field label="Pago">
          {b.paymentProcessor ?? '—'}
          {b.paymentState !== null ? ` · ${b.paymentState}` : ''}
        </Field>
        <Field label="Intent">{mono(b.paymentIntentId)}</Field>
        <Field label="Refund">{mono(b.refundId)}</Field>
        <Field label="Cliente">
          {b.customerName ?? '—'}
          {b.customerEmail !== null ? ` · ${b.customerEmail}` : ''}
        </Field>
        <Field label="Creada">{b.createdAt.slice(0, 10)}</Field>
        <div className="flex justify-end pt-3">
          <ResendButton session={session} bookingId={b.id} />
        </div>
      </div>
    );
  }
  // customer
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-text-secondary">
        {result.customers.length} coincidencia(s) — el mismo email puede existir en varios
        operadores.
      </p>
      {result.customers.map((c) => (
        <div key={c.id} className="flex flex-col rounded-(--nuberu-radius-panel) bg-surface-3 p-3">
          <Field label="Cliente">{c.name}</Field>
          <Field label="Email">{c.email ?? '—'}</Field>
          <Field label="Teléfono">{c.phone}</Field>
          <Field label="Operador">{mono(c.operatorSlug)}</Field>
          <Field label="ID">{mono(c.id)}</Field>
        </div>
      ))}
    </div>
  );
};

export const LookupPanel = ({ session }: { session: Session }): ReactElement => {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<State>({ status: 'idle' });

  const search = async (): Promise<void> => {
    const q = query.trim();
    if (q.length === 0) return;
    setState({ status: 'loading' });
    try {
      const result = await fetchLookup(session, q);
      setState({ status: 'done', result });
    } catch (err: unknown) {
      const message =
        err instanceof ApiError
          ? err.status === 403
            ? 'Tu cuenta no tiene acceso al backoffice.'
            : err.message
          : 'No pudimos completar la búsqueda.';
      setState({ status: 'error', message });
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void search();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Búsqueda de soporte</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex gap-2" onSubmit={onSubmit}>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ID de reserva/operador, slug o email de cliente"
            aria-label="Consulta"
          />
          <Button type="submit" disabled={state.status === 'loading' || query.trim().length === 0}>
            {state.status === 'loading' ? 'Buscando…' : 'Buscar'}
          </Button>
        </form>

        <div className="mt-4">
          {state.status === 'idle' ? (
            <p className="text-sm text-text-secondary">
              Introduce un identificador para localizar una reserva, un operador o un cliente.
            </p>
          ) : state.status === 'error' ? (
            <p className="text-sm text-status-warning" role="alert">
              {state.message}
            </p>
          ) : state.status === 'done' ? (
            <ResultView result={state.result} session={session} />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
